import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Grid, CircularProgress, Button } from '@mui/material';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import Description from '@mui/icons-material/Description';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import Download from '@mui/icons-material/Download';
import { financialDocumentService } from '../services/api';
import { getImageUrl } from '../utils/assets';
import SimplePageBanner from '../components/SimplePageBanner';
import { BRAND } from '../theme/pageStyles';

function fileKind(doc) {
  const mime = String(doc?.file_type || '').toLowerCase();
  const name = String(doc?.original_name || doc?.file_url || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp)$/.test(name)) return 'image';
  return 'doc';
}

function FileIcon({ kind }) {
  if (kind === 'pdf') return <PictureAsPdf sx={{ fontSize: 36, color: '#c2410c' }} />;
  if (kind === 'image') return <ImageOutlined sx={{ fontSize: 36, color: BRAND.green }} />;
  return <Description sx={{ fontSize: 36, color: '#334155' }} />;
}

const FinancialLegalDocumentsPage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    financialDocumentService
      .getAll()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        if (!cancelled) setDocs(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Transparency"
        title="Financial and Legal"
        description="Our commitment to ethical giving, financial accountability, and legal compliance."
      />
      <Box sx={{ backgroundColor: '#FAF9F6', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: BRAND.green }} />
            </Box>
          ) : docs.length === 0 ? (
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                textAlign: 'center',
                color: '#4B4646',
                py: 6,
              }}
            >
              Documents will appear here once they are published.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {docs.map((doc) => {
                const href = getImageUrl(doc.file_url);
                const kind = fileKind(doc);
                return (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        border: '1px solid #EEECE1',
                        boxShadow: '0 10px 28px rgba(0,0,0,0.05)',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '12px',
                          backgroundColor: '#F9F4E8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FileIcon kind={kind} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Nunito", sans-serif',
                          fontWeight: 800,
                          fontSize: 18,
                          color: '#2D2929',
                          lineHeight: 1.35,
                          flex: 1,
                        }}
                      >
                        {doc.title}
                      </Typography>
                      <Button
                        component="a"
                        href={href || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        disabled={!href}
                        startIcon={<Download />}
                        variant="contained"
                        sx={{
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          backgroundColor: BRAND.green,
                          borderRadius: '999px',
                          px: 2.25,
                          '&:hover': { backgroundColor: BRAND.greenHover },
                        }}
                      >
                        Download
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default FinancialLegalDocumentsPage;
